import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { extent } from 'd3-array';
import { useEffect, useMemo, useState } from 'react';
import { useDispatchContext, usePlotContext } from '../hooks';
import { functionalStyle, getNextId, validateAxis } from '../utils';
import ErrorBars from './ErrorBars';
import { markersComps } from './Markers';
export default function ScatterSeries(props) {
    const [id] = useState(() => props.groupId || `series-${getNextId()}`);
    const { xAxis = 'x', yAxis = 'y', data, label, hidden, displayErrorBars = false, ...otherProps } = props;
    // Update plot context with data description
    const { dispatch } = useDispatchContext();
    useEffect(() => {
        const [xMin, xMax] = extent(data, (d) => d.x);
        const [yMin, yMax] = extent(data, (d) => d.y);
        const x = { min: xMin, max: xMax, axisId: xAxis };
        const y = { min: yMin, max: yMax, axisId: yAxis };
        dispatch({ type: 'newData', value: { id, x, y, label, data } });
        // Delete information on unmount
        return () => dispatch({ type: 'removeData', value: { id } });
    }, [dispatch, id, data, xAxis, yAxis, label]);
    if (hidden)
        return null;
    // Render stateless plot component
    const inheritedProps = { data, xAxis, yAxis };
    const errorBarsProps = {
        hidden: !displayErrorBars,
        style: props.errorBarsStyle,
        capStyle: props.errorBarsCapStyle,
        capSize: props.errorBarsCapSize,
    };
    return (_jsxs("g", { children: [_jsx(ErrorBars, Object.assign({}, inheritedProps, errorBarsProps), void 0),
            _jsx(ScatterSeriesRender, Object.assign({}, otherProps, inheritedProps, { id: id }), void 0)] }, void 0));
}
function ScatterSeriesRender({ id, data, xAxis, yAxis, markerShape = 'circle', markerSize = 8, markerStyle = {}, }) {
    // Get scales from context
    const { axisContext, colorScaler } = usePlotContext();
    const [xScale, yScale] = validateAxis(axisContext, xAxis, yAxis);
    // calculates the path to display
    const markers = useMemo(() => {
        if ([xScale, yScale].includes(undefined))
            return null;
        const color = colorScaler(id);
        const defaultColor = { fill: color, stroke: color };
        // Show markers
        const Marker = markersComps[markerShape];
        const markers = data.map((point, i) => {
            const style = functionalStyle(defaultColor, markerStyle, point, i, data);
            return (_jsx("g", Object.assign({ transform: `translate(${xScale(point.x)}, ${yScale(point.y)})` }, { children: _jsx(Marker, { size: markerSize, style: { stroke: style.fill, ...style } }, void 0) }), `markers-${i}`));
        });
        return markers;
    }, [
        xScale,
        yScale,
        colorScaler,
        id,
        data,
        markerSize,
        markerStyle,
        markerShape,
    ]);
    if (!markers)
        return null;
    return _jsx("g", Object.assign({ className: "markers" }, { children: markers }), void 0);
}
//# sourceMappingURL=ScatterSeries.js.map